import { beHttp } from './beHttp';

/**
 * Push notifications de psiplatform mobile (la PWA) — vive en psiplatform-be, no en api-core.
 * Ver psiplatform-be/src/modules/push-notifications. `psychologistId` siempre sale del JWT.
 */

export type PushPlatform = 'android' | 'ios' | 'desktop' | 'other';

export type PushDeviceFormFactor = 'phone' | 'tablet' | 'desktop';
export type PushDeviceOs = 'ios' | 'android' | 'other';
export type PushDeviceBrowser = 'chrome' | 'safari' | 'firefox' | 'edge' | 'other';

/**
 * Info del dispositivo YA derivada (nunca el user agent crudo), opcional en el registro. No cambia `platform`: ese sigue
 * gobernando el reemplazo de tokens y el ícono de psima (una tablet se registra como `desktop` a propósito).
 */
export interface PushDeviceInfo {
  formFactor: PushDeviceFormFactor;
  os: PushDeviceOs;
  /** Aproximada en iOS 26: Safari congela la versión que informa. */
  osVersion?: string;
  model?: string;
  /** `true` si el modelo es una estimación por pantalla (iPhone/iPad); `false` si lo informó el navegador (Android). */
  modelApprox?: boolean;
  browser?: PushDeviceBrowser;
  /** PWA instalada (ventana standalone). */
  installed?: boolean;
  screen?: { w: number; h: number; dpr: number };
}

export interface PushStatus {
  /** Tiene al menos un dispositivo con push activado. */
  enabled: boolean;
  /** Solo si se consultó con `token`: este dispositivo en particular está activado. */
  thisDeviceEnabled: boolean | null;
  /**
   * Solo si se consultó con `token`: el backend YA conocía este token (activo o apagado). `false` = el token rotó
   * (reinstaló, borró datos): se puede volver a registrar sin pedirle nada. Ausente en backends viejos.
   */
  thisDeviceKnown?: boolean | null;
}

/** Por qué este dispositivo no soporta push (lo decide la app por feature detection). */
export type UnsupportedReason = 'no_push_manager' | 'no_notification_api' | 'no_service_worker';

const BASE_PATH = '/api/psychologist/push-notifications';

export function fetchPushStatus(token?: string): Promise<PushStatus> {
  return beHttp.get<PushStatus>(`${BASE_PATH}/status`, token ? { token } : undefined, {
    showErrorToast: false,
  });
}

export function registerPushDevice(token: string, platform: PushPlatform, device?: PushDeviceInfo): Promise<PushStatus> {
  return beHttp.put<PushStatus>(`${BASE_PATH}/device`, { token, platform, ...(device ? { device } : {}) }, { showErrorToast: false });
}

export function disablePushDevice(token: string): Promise<PushStatus> {
  return beHttp.post<PushStatus>(`${BASE_PATH}/device/disable`, { token }, { showErrorToast: false });
}

/** Avisa que la app se abrió en un dispositivo SIN soporte de push (la app no se bloquea; psima lo ve en el backoffice). */
export function reportUnsupportedPushDevice(body: {
  platform: PushPlatform;
  osVersion?: string;
  reason: UnsupportedReason;
}): Promise<{ reported: true }> {
  return beHttp.put<{ reported: true }>(`${BASE_PATH}/unsupported-device`, body, { showErrorToast: false });
}
